import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import clientService from '../services/clientService';
import { ClienteRequestDTO, ClienteResponseDTO } from '../types/client';
import alertService from '../services/alertService';
import { useNavigate, useParams } from 'react-router-dom';

const ClientInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'novo';
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<ClienteRequestDTO>({ nome: '', documento: '', email: '', telefone: '', endereco: '', ativo: true });
  const [tipoPessoa, setTipoPessoa] = useState<'FISICA' | 'JURIDICA'>('FISICA');

  const formatCpf = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 11);
    if (v.length <= 3) return v;
    if (v.length <= 6) return v.replace(/(\d{3})(\d+)/, '$1.$2');
    if (v.length <= 9) return v.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  };

  const formatCnpj = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 14);
    if (v.length <= 2) return v;
    if (v.length <= 5) return v.replace(/(\d{2})(\d+)/, '$1.$2');
    if (v.length <= 8) return v.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
    if (v.length <= 12) return v.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
    return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const handleChangeTipoPessoa = (tipo: 'FISICA' | 'JURIDICA') => {
    // Em clientes já cadastrados não permitimos alterar o tipo
    if (!isNew) return;
    setTipoPessoa(tipo);
    const clean = (form.documento || '').replace(/[^\d]/g, '');
    const adapted = tipo === 'FISICA' ? clean.slice(0, 11) : clean.slice(0, 14);
    const formatted = tipo === 'FISICA' ? formatCpf(adapted) : formatCnpj(adapted);
    setForm({ ...form, documento: formatted });
  };

  const carregar = async () => {
    if (isNew) { setLoading(false); return; }
    try {
      setLoading(true);
      const resp = await clientService.getById(id!);
      const apiData: any = resp?.data as any;
      if (apiData) {
        const cpf: string = apiData.cpf || '';
        const cnpj: string = apiData.cnpj || '';
        const docRaw: string = cpf || cnpj || apiData.documento || '';
        const tipoFromApi = apiData.tipoPessoa === 'JURIDICA' ? 'JURIDICA' : (apiData.tipoPessoa === 'FISICA' ? 'FISICA' : undefined);
        const clean = (docRaw || '').replace(/[^\d]/g, '');
        const tipo: 'FISICA' | 'JURIDICA' = (tipoFromApi ?? (clean.length === 14 ? 'JURIDICA' : 'FISICA')) as any;
        setTipoPessoa(tipo);
        const formatted = tipo === 'FISICA' ? formatCpf(docRaw) : formatCnpj(docRaw);
        setForm({ nome: apiData.nome, documento: formatted, email: apiData.email, telefone: apiData.telefone, endereco: apiData.endereco, ativo: apiData.ativo });
      }
    } catch (err: any) {
      alertService.error('Erro ao carregar cliente', err?.message || 'Tente novamente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const salvar = async () => {
    try {
      const documento = (form.documento || '').trim();
      if (!documento) {
        alertService.error('Documento obrigatório', 'Informe CPF (Pessoa Física) ou CNPJ (Pessoa Jurídica)');
        return;
      }
      const docClean = documento.replace(/[^\d]/g, '');
      if (tipoPessoa === 'FISICA') {
        if (docClean.length !== 11) {
          alertService.error('CPF inválido', 'CPF deve ter 11 dígitos');
          return;
        }
      } else {
        if (docClean.length !== 14) {
          alertService.error('CNPJ inválido', 'CNPJ deve ter 14 dígitos');
          return;
        }
      }
      alertService.loading('Salvando cliente...');
      const payload = { ...form, documento: docClean };
      if (isNew) {
        await clientService.create(payload);
      } else {
        await clientService.update(id!, payload);
      }
      alertService.close();
      alertService.success('Cliente salvo');
      navigate('/clientes');
    } catch (err: any) {
      alertService.close();
      alertService.error('Erro ao salvar', err?.message || 'Tente novamente');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Users className="h-6 w-6 text-red-600" />
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isNew ? 'Novo Cliente' : 'Editar Cliente'}</h1>
          <p className="text-sm text-gray-600">Cadastre ou edite os dados do cliente</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Pessoa</label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2">
                <input type="radio" checked={tipoPessoa === 'FISICA'} onChange={() => handleChangeTipoPessoa('FISICA')} disabled={!isNew} />
                <span>Pessoa Física</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" checked={tipoPessoa === 'JURIDICA'} onChange={() => handleChangeTipoPessoa('JURIDICA')} disabled={!isNew} />
                <span>Pessoa Jurídica</span>
              </label>
            </div>
            {!isNew && (
              <p className="text-xs text-gray-500 mt-1">Para clientes cadastrados, o tipo de pessoa não pode ser alterado.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tipoPessoa === 'FISICA' ? 'CPF' : 'CNPJ'}</label>
            {tipoPessoa === 'FISICA' ? (
              <input value={formatCpf(form.documento || '')} onChange={(e) => setForm({ ...form, documento: formatCpf(e.target.value) })} placeholder="000.000.000-00" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            ) : (
              <input value={formatCnpj(form.documento || '')} onChange={(e) => setForm({ ...form, documento: formatCnpj(e.target.value) })} placeholder="00.000.000/0000-00" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <input value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
            <input value={form.endereco || ''} onChange={(e) => setForm({ ...form, endereco: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={() => navigate('/clientes')} className="px-4 py-2 border rounded-lg">Cancelar</button>
          <button onClick={salvar} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">Salvar</button>
        </div>
      </div>
    </div>
  );
};

export default ClientInfoPage;